/**
 * Infinite scroll hook using Intersection Observer API.
 * Triggers a callback when the observed element enters the viewport.
 */

import { useEffect, useRef, useCallback, useState } from 'react';

export const useInfiniteScroll = (onLoadMore, hasMore, isLoading) => {
  const observerRef = useRef(null);
  const [loadMoreElement, setLoadMoreElement] = useState(null);

  // Callback ref to track when element is mounted/unmounted
  const loadMoreRef = useCallback((node) => {
    setLoadMoreElement(node);
  }, []);

  // Handle intersection
  const handleObserver = useCallback(
    (entries) => {
      const [target] = entries;

      if (target.isIntersecting && hasMore && !isLoading) {
        onLoadMore();
      }
    },
    [onLoadMore, hasMore, isLoading]
  );

  // Set up Intersection Observer
  useEffect(() => {
    // Don't observe if element doesn't exist or no more items to load
    if (!loadMoreElement || !hasMore) {
      return;
    }

    const options = {
      root: null, // viewport
      rootMargin: '100px', // Trigger earlier before element enters viewport
      threshold: 0, // Trigger as soon as any part is visible
    };

    observerRef.current = new IntersectionObserver(handleObserver, options);
    observerRef.current.observe(loadMoreElement);

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loadMoreElement, handleObserver, hasMore, isLoading]);

  return loadMoreRef;
};
